
import { defineComponent } from "vue";
import { store } from "@/store";
import BasePanel from "@/components/BasePanel.vue";
import {
	IonBackButton,
	IonButton,
	IonButtons,
	IonContent,
	IonHeader,
	IonImg,
	IonInput,
	IonList,
	IonPage,
	IonRippleEffect,
	IonSearchbar,
	IonSlide,
	IonSlides,
	IonTextarea,
	IonThumbnail,
	IonTitle,
	IonToolbar,
} from "@ionic/vue";
import ProductListItem from "@/components/ProductListItem.vue";
import InAppCameraModal from "@/components/InAppCameraModal.vue";
import PhotoCaptureService from "@/services/photo-capture.service";
import CardModal from "@/components/CardModal.vue";
import OrderService, {
	OrderRefreshResult,
} from "@/services/order.service";
import BaseLoader from "@/components/BaseLoader.vue";
import debounce from "lodash/debounce";
import ToastService from "@/services/toast.service";
import dayjs from "dayjs";
import { toDialablePhone } from "@/utils/phone";
import ImageLoaderOverlay from "@/components/ImageLoaderOverlay.vue";
import SheetModal from "@/components/SheetModal.vue";
import DefectivePartsSection from "@/components/DefectivePartsSection.vue";
// @ts-ignore
import * as Sentry from "@sentry/capacitor";

export default defineComponent({
	name: "OrderDetail",
	components: {
		InAppCameraModal,
		DefectivePartsSection,
		SheetModal,
		ImageLoaderOverlay,
		BaseLoader,
		ProductListItem,
		CardModal,
		BasePanel,
		IonContent,
		IonPage,
		IonInput,
		IonSearchbar,
		IonTitle,
		IonButtons,
		IonBackButton,
		IonHeader,
		IonToolbar,
		IonSlides,
		IonSlide,
		IonImg,
		IonThumbnail,
		IonButton,
		IonList,
		IonRippleEffect,
		IonTextarea,
	},

	props: {
		id: {
			type: [String, Number],
			required: true,
		},
		previousOrderId: {
			type: Number,
			default: null,
		},
	},

	data() {
		return {
			hasDefectiveParts: false,
			dateOptions: { year: "numeric", month: "long", day: "numeric" },
			BackIcon: require("@/assets/images/icons/back.svg"),
			SearchIcon: require("@/assets/images/icons/search.svg"),
			updatedEmail: "",
			imageSlideOptions: {
				initialSlide: 1,
				zoom: {
					maxRatio: 4,
				},
			},
			destinationCoordinates: {
				lat: 0.0,
				lng: 0.0,
			},
			amountOfLoadedImages: 0,
			showInstallationInformation: false,
			showServiceInformation: false,
			searchbarInput: "",
			copyOfImages: [] as any[],
			isDisabled: false,
			isFiltering: false,
			selectedImage: { uploadedAt: "1 jan. 2999", url: null },
			isOrderLineWatcherDisabled: false,
		};
	},

	methods: {
		dialablePhone: toDialablePhone,

		/**
		 * Validates the defective parts and sends them along. Registering them is
		 * part of moving on, so a part left open is sent, not forgotten.
		 */
		async sendDefectiveParts(): Promise<boolean> {
			const section = this.$refs.defectivePartsSection as any;

			if (!section || !this.canEditDefectiveParts) {
				return true;
			}

			const problem = section.validate();

			if (problem) {
				ToastService.presentToast(problem, 4000, "danger");

				return false;
			}

			return await section.send();
		},

		onDefectivePartsLoaded(count: number) {
			if (count > 0) {
				this.hasDefectiveParts = true;
			}
		},

		/**
		 * Sorts the order lines by price asc.
		 */
		sortedOrderLines(orderLines: any[], addedByMechanic: number | null) {
			// sort the orderLines by price descending and check if the is_updated value equals updatedByMechanic.
			return [...orderLines].filter((orderLine: any) => {
				if (addedByMechanic) {
					return orderLine.is_added_by_mechanic === addedByMechanic;
				}

				return !orderLine.is_added_by_mechanic;
			});
		},

		/**
		 * Update the mechanic comment of the order with the receiving value from the ion-textarea for the comment
		 */
		updateMechanicComment(value: string) {
			store.commit("order/updateOrderMechanicComment", value);
		},
		/**
		 * Update the customers e-mail address.
		 */
		updateEmailAddress() {
			if (this.validEmail(this.updatedEmail)) {
				OrderService.updateCustomerEmailAddress(
					this.order.id,
					this.updatedEmail
				)
					.then(async (result: OrderRefreshResult) => {
						if (!result.actionSucceeded) {
							await ToastService.presentToast(
								"Het e-mailadres kon niet worden gewijzigd",
								3500,
								"danger"
							);

							return;
						}

						if (!result.orderLinesArePersisted) {
							await this.warnOrderLinesNotSaved();
						}
					})
					.finally(() => {
						(this.$refs["edit-email-modal"] as any).isOpen = false;
					});
			} else {
				ToastService.presentToast(
					"Voer een valide e-mailadres in",
					3500,
					"danger"
				);
			}
		},

		/**
		 * is this group in the selected addable product group.
		 * @param groupId
		 */
		isGroupInSelectedAddableProductGroup(groupId: number) {
			return this.selectedAddableProductGroup.find(
				(group: number) => group === groupId
			);
		},

		/**
		 * Set image loaded.
		 * @param image
		 */
		setImageLoaded(image: any) {
			image.loaded = true;
		},

		/**
		 * Checks if an emailaddress is valid.
		 * @param email
		 */
		validEmail: function (email: string) {
			const re =
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return re.test(email);
		},

		/**
		 * Opens the in-app camera.
		 */
		async openCamera() {
			await (this.$refs["in-app-camera"] as any).open();
		},

		/**
		 * Opens the edit email modal.
		 */
		openEditEmailModal() {
			(this.$refs["edit-email-modal"] as any).isOpen = true;
		},

		/**
		 * Creates a comparable boolean from a value that is supposed to be a boolean.
		 * @param boolean
		 */
		booleanFormatter(boolean: boolean | number | string) {
			switch (boolean) {
				case 0:
					return false;
				case 1:
					return true;
				case "true":
					return true;
				case "false":
					return false;
				case "0":
					return false;
				case "1":
					return true;
				default:
					return boolean;
			}
		},

		/**
		 * Formats a given value to a more readable value, to show to the mechanic.
		 * @param value
		 */
		formatInformationValue(value: string | boolean) {
			switch (value) {
				case "true":
					return "Ja";
				case "false":
					return "Nee";
				case false:
					return "Nee";
				case true:
					return "Ja";
				case null:
					return "-";
				case undefined:
					return "-";
				default:
					return value;
			}
		},

		/**
		 * Opens the image modal.
		 * @param image
		 * @param imageIndex
		 */
		openImageModal(image: any, imageIndex: number) {
			// Sets the select image to be the one that's clicked upon.
			this.selectedImage = image;

			// Set the initial slide to the given index.
			this.imageSlideOptions.initialSlide = imageIndex;

			// The images in the slider are not loaded yet, we want the value to be false before we load them.
			// image.loaded = false;

			// Open modal.
			(this.$refs["show-image-modal"] as any).isOpen = true;
		},

		/**
		 * Sets a new selected image
		 * @param image
		 */
		setSelectedImage(image: any) {
			this.selectedImage = image;
		},

		/**
		 * Syncs the selected image with the active slide.
		 * @param event
		 */
		async onImageSlideChanged(event: any) {
			const activeIndex = await event.target.getActiveIndex();

			if (this.copyOfImages[activeIndex]) {
				this.setSelectedImage(this.copyOfImages[activeIndex]);
			}
		},

		/**
		 * Opens the product modal.
		 */
		async openProductModal() {
			(this.$refs["add-product-modal"] as any).isOpen = true;
		},

		/**
		 * Adds selected products to the appointment/order and closes modal.
		 */
		async addSelectedProductsToOrder() {
			await store.dispatch("order/addSelectedProductsToOrder");
			(this.$refs["add-product-modal"] as any).isOpen = false;
		},

		/**
		 * Toggle the product selection.
		 * @param sku
		 */
		toggleProductSelection(sku: number) {
			store.commit("order/toggleSelectionOfCertainProduct", {
				productSku: sku,
			});
		},

		/**
		 * Toggle the accordion for installation information.
		 */
		toggleInstallationInformation() {
			this.showInstallationInformation =
				!this.showInstallationInformation;
		},

		/**
		 * Toggle the accordion for service information.
		 */
		toggleServiceInformation() {
			this.showServiceInformation = !this.showServiceInformation;
		},

		beforeEnter(el: any) {
			el.style.height = "0";
		},

		enter(el: any) {
			el.style.height = `${el.scrollHeight}px`;
		},

		beforeLeave(el: any) {
			el.style.height = `${el.scrollHeight}px`;
		},

		leave(el: any) {
			el.style.height = "0";
		},

		/**
		 * Upload a single new image immediately after it's taken.
		 */
		async uploadNewImage(imageElement: any) {
			// Set loading state for this specific image
			imageElement.isUploading = true;

			// Uploading refreshes the order afterwards, which would discard a
			// price or quantity the mechanic has not saved yet, so the service
			// saves those first.
			const result = await OrderService.uploadSituationImage(
				imageElement.file
			);

			imageElement.isUploading = false;

			if (!result.actionSucceeded) {
				// isNewImage stays true: the photo never reached the server, so
				// the mechanic must still be able to remove it from the grid.
				Sentry.captureException(result.error);
				await ToastService.presentToast(
					"Er is een fout opgetreden bij het opslaan van de foto",
					2500,
					"danger"
				);

				return;
			}

			// Mark the image as uploaded
			imageElement.isNewImage = false;

			if (!result.orderLinesArePersisted) {
				await this.warnOrderLinesNotSaved();
			}
		},

		/**
		 * Update the images.
		 */
		updateImages: async function () {
			await store.dispatch("general/setIsUpdating", { isUpdating: true });

			// First we update the order, this is because the mechanic might have added products or changed quantities/prices.
			await OrderService.updateOrder();

			// Check if one of the images is a newly added image.
			const hasNewUploadedImages = this.copyOfImages.some(
				(image: any) => image.isNewImage
			);

			// If we have new images, we need to upload them.
			if (hasNewUploadedImages) {
				try {
					const newImages = this.copyOfImages.filter(
						(image: any) => image.isNewImage
					);
					await OrderService.updateAllImagesAndFetchOrder(newImages);

				// Set them all to not be new images.
				this.copyOfImages.forEach((image: any) => {
					image.isNewImage = false;
				});
			} catch (error) {
				Sentry.captureException(error);
				await ToastService.presentToast(
					"Er is een fout opgetreden bij het opslaan van de foto's",
					2500,
					"danger"
				);
			}
			}

			await store.dispatch("general/setIsUpdating", {
				isUpdating: false,
			});
		},

		/**
		 * Calls the update order event with a debounce.
		 */
		updateOrder: debounce(async () => {
			await store.dispatch("general/setIsUpdating", { isUpdating: true });
			await OrderService.updateOrder()
				.then(async () => {
					await ToastService.presentToast(
						"Wijzigingen opgeslagen",
						2500,
						"success",
						"top"
					);
				})
				.finally(async () => {
					await store.dispatch("general/setIsUpdating", {
						isUpdating: false,
					});
					store.commit("order/setOrderLinesHaveChanged", {
						orderLinesHaveChanged: false,
					});
				});
		}, 500),

		toNextStepFinished() {
			// Checks if we have a product in our order line that requires a serial number.
			const serialNumberProduct = this.order.order_lines.find(
				(orderLine: any) => {
					return orderLine.product.has_serial_numbers === true;
				}
			);

			if (serialNumberProduct) {
				this.$router.push({
					name: "ProductSerialNumber",
					params: { id: this.order.id },
				});
			} else {
				this.$router.push({
					name: "OrderAgreement",
					params: { id: this.order.id },
				});
			}
		},

		uppercaseFirstLetter(name: string) {
			return name.charAt(0).toUpperCase() + name.slice(1);
		},

		/**
		 * Triggered when user clicks on the 'To next step' button
		 */
		async toNextStep() {
			if (this.isUpdating) {
				return;
			}

			this.isOrderLineWatcherDisabled = true;
			// Notifies the user we are currently doing things, through loading indicator.
			await store.dispatch("general/setIsUpdating", {
				isUpdating: true,
			});

			// A failed send stays on this step, so the mechanic can correct it.
			if (!(await this.sendDefectiveParts())) {
				this.isOrderLineWatcherDisabled = false;
				await store.dispatch("general/setIsUpdating", {
					isUpdating: false,
				});

				return;
			}

			// Checks if we have a product in our order line that requires a serial number.
			const serialNumberProduct = this.order.order_lines.find(
				(orderLine: any) => {
					return orderLine.product.has_serial_numbers === true;
				}
			);

			// If we have at least one product that needs a serial number;
			// Updates the order and pushes to the serial number step.
			OrderService.updateOrder()
				.then((res) => {
					if (res) {
						if (serialNumberProduct) {
							this.$router.push({
								name: "ProductSerialNumber",
								params: { id: this.order.id },
							});
						} else {
							this.$router.push({
								name: "OrderAgreement",
								params: { id: this.order.id },
							});
						}
					}
				})
				.finally(() => {
					store.dispatch("general/setIsUpdating", {
						isUpdating: false,
					});
				});
		},

		/**
		 * Formats a price value.
		 * @param price
		 */
		priceFormatting(price: any) {
			return new Intl.NumberFormat("nl-NL", {
				style: "currency",
				currency: "EUR",
				currencySign: "accounting",
			}).format(price);
		},

		/**
		 * Remove an images from the images
		 * @param imageIndex
		 */
		removeImage(imageIndex: number) {
			const image = this.copyOfImages[imageIndex];

			if (image.isNewImage) {
				// Captured photos are shown through an object URL; release it
				// here too, so removed images do not hold their blob until
				// unmount.
				if (
					typeof image.src === "string" &&
					image.src.startsWith("blob:")
				) {
					URL.revokeObjectURL(image.src);
				}

				this.copyOfImages.splice(imageIndex, 1);
			} else {
				ToastService.presentToast(
					"Deze afbeelding kan niet worden verwijderd",
					3500,
					"danger"
				);
			}
		},

		/**
		 * Warns that the change went through but the order line edits did not.
		 *
		 * Without this the mechanic only finds out at checkout, by which point
		 * they have no idea which price went back to what.
		 */
		async warnOrderLinesNotSaved() {
			await ToastService.presentToast(
				"De wijziging in de producten is nog niet opgeslagen. Sla deze op met 'Wijziging opslaan'.",
				3500,
				"warning"
			);
		},

		/**
		 * Uploads a photo handed over by the in-app camera.
		 *
		 * The overlay has already scaled and re-encoded it, so there is nothing
		 * left to normalise here.
		 * @param photo
		 */
		async onPhotoCaptured(photo: File) {
			const src = URL.createObjectURL(photo);

			try {
				await this.addCapturedImage(photo, src);
			} catch (error) {
				// The image never made it into the grid, so nothing else
				// (removeImage, unmount) will release this URL.
				URL.revokeObjectURL(src);
				PhotoCaptureService.reportCaptureFailure(error);
			}
		},

		/**
		 * Adds a captured image to the list and uploads it.
		 * @param blob
		 * @param src
		 */
		async addCapturedImage(blob: Blob, src: string) {
			const imageElement = {} as any;

			imageElement.src = src; // Used for display
			// Keep an incoming File as-is: the re-encoding fallback can hand
			// back the original (possibly HEIC) file, and relabelling it as
			// JPEG would lie to the backend about its type.
			imageElement.file =
				blob instanceof File
					? blob
					: new File([blob], "photo.jpg", {
						type: blob.type || "image/jpeg",
					}); // Stored for upload
			imageElement.isNewImage = true;
			imageElement.loaded = false;
			imageElement.isUploading = false;
			imageElement.uploadedAt = dayjs(new Date()).format("DD MMM. YYYY");

			// Set it to the copy of images
			this.copyOfImages.unshift(imageElement);

			// Automatically upload the image
			await this.uploadNewImage(imageElement);
		},

		/**
		 * Format a price value.
		 * @param price
		 */
		formatPrice(price: number) {
			return new Intl.NumberFormat("nl-NL", {
				style: "currency",
				currency: "EUR",
				currencySign: "accounting",
			}).format(price);
		},

		/**
		 * Navigate to location.
		 */
		async navigateToLocation() {
			const destination = [
				this.order.customer.country_code,
				this.order.customer.city,
				this.order.customer.streetname,
				[
					this.order.customer.house_number,
					this.order.customer.house_number_addition,
				]
					.filter((v) => v !== null)
					.join("-"),
			].join(" ");

			await window.open(
				`https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${destination}`
			);
		},

		/**
		 * Add the value to the selectedProductGroup to switch categories
		 * @param value
		 */
		setSelectedProductGroup(value: any) {
			store.commit("order/setSelectedAddableProductGroup", {
				addableProductGroupId: value.id,
			});
		},

		/**
		 * Toggle the history modal.
		 */
		toggleHistoryModal() {
			if (this.order.appointment_history.length >= 1) {
				(this.$refs["open-history-modal"] as any).isOpen = !(
					this.$refs["open-history-modal"] as any
				).isOpen;
			}
		},

		/**
		 * Move to the order detail page.
		 * @param item
		 */
		async moveToOrderDetail(item: any) {
			// Store current order ID before making the API call
			const currentOrderId = this.order.id;

			// Get the new order data
			await OrderService.getOrderById(item.id).finally(() => {
				OrderService.fetchAddableProductGroups();
				this.$router.push({
					name: "OrderDetail",
					params: { id: item.id },
					query: { previousOrderId: currentOrderId },
				});
				(this.$refs["open-history-modal"] as any).isOpen = false;
			});
		},

		/**
		 * Handle back button click
		 */
		async handleBackClick() {
			if (this.previousOrderId) {
				await OrderService.getOrderById(this.previousOrderId).finally(
					() => {
						OrderService.fetchAddableProductGroups();
						this.$router.push({
							name: "OrderDetail",
							params: { id: this.previousOrderId },
						});
					}
				);
			} else {
				// Otherwise go to the root
				this.$router.push("/");
			}
		},
	},

	computed: {
		installationTitle() {
			if (
				this.order.installation_information &&
				this.order.service_information
			) {
				return "Oorspronkelijke installatie situatie";
			}
			return "Installatie informatie";
		},

		authenticatedUserIsPlanner() {
			return store.getters["auth/getUserIsPlanner"];
		},

		userId() {
			return store.getters["auth/getUserId"];
		},

		user() {
			return store.getters["general/getUser"];
		},

		order() {
			return store.getters["order/getSelectedOrder"];
		},

		/**
		 * Whether this user may still add photos to this order.
		 */
		/**
		 * Same rule as the other fields of the order: a planner who is not the
		 * mechanic of the appointment cannot move on, so cannot send parts either.
		 */
		canEditDefectiveParts() {
			return (
				!this.order.is_finished &&
				(!this.authenticatedUserIsPlanner ||
					this.userId === this.order.mechanic_id)
			);
		},

		canAddImages() {
			return (
				!this.order.is_finished &&
				(!this.authenticatedUserIsPlanner ||
					this.userId === this.order.mechanic_id)
			);
		},


		orderLineHasChanged() {
			return store.getters["order/getOrderLinesHaveChanged"];
		},

		orderLines() {
			return JSON.parse(
				JSON.stringify(
					store.getters["order/getSelectedOrder"].order_lines
				)
			);
		},

		isUpdating() {
			return store.getters["general/getIsUpdating"];
		},

		sum() {
			return (
				store.getters["order/getSelectedOrder"].installation_information
					.length - 1
			);
		},

		serviceInformationSum() {
			return (
				store.getters["order/getSelectedOrder"].service_information
					.length - 1
			);
		},

		selectedAddableProducts() {
			return store.getters["order/getSelectedAddableProducts"];
		},

		totalPrice() {
			const totalPrice = store.getters["order/getTotalPrice"];

			return new Intl.NumberFormat("nl-NL", {
				style: "currency",
				currency: "EUR",
				currencySign: "accounting",
			}).format(totalPrice);
		},

		addableProducts: {
			get() {
				return store.getters["order/getAddableProducts"];
			},

			set() {
				return store.getters["order/getAddableProducts"];
			},
		},

		addableProductGroups() {
			return store.getters["order/getAddableProductGroups"];
		},

		/**
		 * Every addable product, without the group and search filters of the add-product modal.
		 */
		allAddableProducts() {
			return store.getters["order/getAllAddableProducts"];
		},

		selectedAddableProductGroup() {
			return store.getters["order/getSelectedAddableProductGroups"];
		},
	},
	mounted() {
		this.searchbarInput = store.getters["getProductSearchbarInput"];
		this.updatedEmail = this.order.customer.email;
		this.copyOfImages = [...this.order.images].map((image: any) => {
			return {
				...image,
				isNewImage: false,
				loaded: false,
				isUploading: false,
			};
		});

		// We don't want to be updating when we enter the page.
		store.commit("general/setIsUpdating", { isUpdating: false });
	},
	beforeUnmount() {
		// Captured photos are shown through object URLs; release them so
		// repeated captures do not keep their blobs alive.
		this.copyOfImages.forEach((image: any) => {
			if (typeof image.src === "string" && image.src.startsWith("blob:")) {
				URL.revokeObjectURL(image.src);
			}
		});
	},
	beforeRouteLeave() {
		this.isOrderLineWatcherDisabled = false;
	},

	watch: {
		searchbarInput() {
			store.commit("order/setProductSearchBarInput", {
				searchbarInput: this.searchbarInput,
			});

			this.addableProducts = store.getters["order/getAddableProducts"];
		},
		orderLines(current, old) {
			if (JSON.stringify(current) !== JSON.stringify(old)) {
				store.commit("order/setOrderLinesHaveChanged", {
					orderLinesHaveChanged: true,
				});
			}
		},
	},
});
